import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as SchedulerActions from './scheduler.actions';
import * as AppActions from '../app/actions';
import { SchedulerService } from '../../shared/service/scheduler/scheduler.service';
import { Store } from '@ngrx/store';
import * as oeeAppReducer from '../oee.reducer';
import { ActivitiesInterface } from '../../shared/model/interface/activities.model';
import {
  BaseOneResponseInterface,
  BulkResponseDataInterface,
  GetManyResponseInterface,
} from '../../shared/model/interface/crud-response-interface.model';
import {
  ICheckForDeployResponse,
  IScenarioGanttAllItemsResponse,
  ISchedulerKpiMetricInfo,
  ISchedulerOptimizationResponse,
  ILinePathDetailsBySiteId,
  ISchedulerShift,
  IOptimizationAlgorithms,
  ScenarioInterface,
  ESchedulerOptimizationStatus,
} from './scheduler.model';
import { ScenarioBulkResponseDataInterface } from '../scheduler-scenario/scheduler-scenario.model';
import { ResponseInterface } from '../../shared/model/interface/generic-api-response.model';
import { EntityTranslatorService } from '../../shared/service/entity-translator/entity-translator.service';
import { emptyAction } from '../../../constants';
import { SchedulerWorkOrderSetService } from '../../shared/service/scheduler-work-order-set/scheduler-work-order-set.service';
import { IWorkOrderScheduleWithRules } from '../scheduler-work-order-set/scheduler-work-order-set.model';
import { ICheckForIncompatibleWorkOrdersResponse } from '../../view/scheduler/production-gantt-view/production-gantt-view.component.model';
import { HelperService } from '../../shared/service/helper.service';
import { HttpParams } from '@angular/common/http';
import { ProductFamilyService } from '../settings/product-family/product-family.service';
import { IProductFamily } from '../settings/product-family/product-family.model';

@Injectable()
export class SchedulerEffects {
  constructor(
    private readonly actions$: Actions<SchedulerActions.SchedulerActions>,
    private readonly schedulerService: SchedulerService,
    private readonly helperService: HelperService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly entityTranslator: EntityTranslatorService,
    private readonly schedulerWorkOrderSetService: SchedulerWorkOrderSetService,
    private readonly productFamilyService: ProductFamilyService,
  ) {}

  $loadScenarios = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.LoadScenario),
      switchMap((objectData: SchedulerActions.LoadScenario) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.schedulerService.loadScenario(objectData.scenarioId).pipe(
          switchMap((response: BaseOneResponseInterface<ScenarioInterface>) => {
            return of(
              ...[
                new SchedulerActions.ScenarioLoaded(response),
                new AppActions.HideLoader(),
                ...(objectData.isKpiMetricInfoLoad
                  ? [new SchedulerActions.KpiMetricInfoLoad(objectData.scenarioId)]
                  : []),
              ],
            );
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $loadScenarioItems = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.LoadScenarioItems),
      switchMap((objectData: SchedulerActions.LoadScenarioItems) => {
        return this.schedulerService
          .loadScenarioGanttAllItems(
            objectData.scenario,
            objectData.startDate,
            objectData.endDate,
            objectData.loadOptions,
          )
          .pipe(
            switchMap((response: IScenarioGanttAllItemsResponse) => {
              this.schedulerService.translateScenarioData(response);

              return of(new SchedulerActions.ScenarioItemsLoaded(response));
            }),
            catchError((error) => {
              return of(new SchedulerActions.FetchError(error));
            }),
          );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $saveScenarioItems = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.SaveScenarioItems),
      switchMap((objectData: SchedulerActions.SaveScenarioItems) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.schedulerService.saveScenarioItems(objectData.scenarioId, objectData.scenarioItems).pipe(
          switchMap((response) => {
            return of(
              ...[
                new SchedulerActions.ScenarioItemsSaved(response, {
                  scenarioId: objectData.scenarioId,
                  scenarioItems: objectData.scenarioItems,
                  triggeredByKPI: objectData.triggeredByKPI,
                  linesToDeploy: objectData.linesToDeploy,
                }),
                ...(objectData?.linesToDeploy?.length ? [] : [new AppActions.HideLoader()]),
              ],
            );
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );

  $saveSchedulerKpiCardData = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.SaveSchedulerKpiCardData),
      switchMap((objectData: SchedulerActions.SaveSchedulerKpiCardData) => {
        return this.schedulerService
          .saveSchedulerKpiCardData(objectData.payload.scenarioId, objectData.payload.data)
          .pipe(
            switchMap((response: BaseOneResponseInterface<ScenarioInterface>) => {
              return of(new SchedulerActions.SchedulerKpiCardDataSaved(response));
            }),
            catchError((error) => {
              return of(new SchedulerActions.FetchError(error));
            }),
          );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $deployScenarioItems = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.DeployScenarioItems),
      switchMap((objectData: SchedulerActions.DeployScenarioItems) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.schedulerService
          .deploy(objectData.horizon, objectData.deployedItems, objectData.siteId, objectData.uncreatedWorkOrders)
          .pipe(
            switchMap((response: BulkResponseDataInterface) => {
              return of(
                new SchedulerActions.ScenarioItemsDeployed(response.data, response.success),
                new AppActions.HideLoader(),
              );
            }),
            catchError((error) => {
              return of(new SchedulerActions.FetchError(error), new AppActions.HideLoader());
            }),
          );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );

  $loadDownTimeActivitiesData = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.LoadDownTimeActivitiesData),
      switchMap((objectData: SchedulerActions.LoadDownTimeActivitiesData) => {
        return this.schedulerService.getDownTimeActivities(objectData.scenarioId).pipe(
          switchMap((response: GetManyResponseInterface<ActivitiesInterface>) => {
            response.data.forEach((activities: ActivitiesInterface) => {
              this.entityTranslator.translate(activities);
            });
            return of(new SchedulerActions.DownTimeActivitiesDataLoaded(response.data));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $loadPlansData = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.LoadScenarioShiftPlans),
      switchMap((objectData: SchedulerActions.LoadScenarioShiftPlans) => {
        return this.schedulerService.loadPlans(objectData.scenarioId).pipe(
          switchMap((response: BaseOneResponseInterface<ISchedulerShift[]>) => {
            response.data = response.data.map((item: ISchedulerShift) => {
              return {
                ...item,
                startDate: this.helperService.convertFromISOFormatToGivenTimezone(item.startDate),
                endDate: this.helperService.convertFromISOFormatToGivenTimezone(item.endDate),
              };
            });
            return of(new SchedulerActions.ScenarioShiftPlansLoaded(response.data));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $saveManyScenarioWorkOrderRules = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.SaveManyScenarioWorkOrderRules),
      switchMap((objectData: SchedulerActions.SaveManyScenarioWorkOrderRules) => {
        return this.schedulerService.saveManyScenarioWorkOrderRules(objectData.payload).pipe(
          switchMap((response: ScenarioBulkResponseDataInterface) => {
            return of(new SchedulerActions.ScenarioWorkOrderRulesAdded(response));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $setAsDefault = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.AddScenarioAdvancedFilter),
      switchMap((objectData: SchedulerActions.AddScenarioAdvancedFilter) => {
        return this.schedulerService.setAsDefault(objectData.advancedFilter).pipe(
          switchMap((response: BaseOneResponseInterface<ScenarioInterface>) => {
            return of(new SchedulerActions.ScenarioAdvancedFilterAdded(response));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $loadUnplannedWorkOrderData = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.LoadUnplannedWorkOrderData),
      switchMap((objectData: SchedulerActions.LoadUnplannedWorkOrderData) => {
        return this.schedulerWorkOrderSetService.getWorkOrdersForSchedulerRules(objectData.filters).pipe(
          switchMap((response: GetManyResponseInterface<IWorkOrderScheduleWithRules>) => {
            response.data.forEach((workOrder: IWorkOrderScheduleWithRules) =>
              this.entityTranslator.translate(workOrder),
            );
            const formattedUnPlannedWorkOrders: IWorkOrderScheduleWithRules[] =
              this.schedulerWorkOrderSetService.formatUnPlannedWorkOrders(response.data);

            return of(new SchedulerActions.UnplannedWorkOrderDataLoaded(formattedUnPlannedWorkOrders));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $optimizeScheduler = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.OptimizeScheduler),
      mergeMap((objectData: SchedulerActions.OptimizeScheduler) => {
        return this.schedulerService.optimizeScheduler(objectData.scenarioId, objectData.optimizationType).pipe(
          mergeMap((response: ResponseInterface<ISchedulerOptimizationResponse>) => {
            return of(
              new SchedulerActions.SchedulerOptimized(response.data),
              new SchedulerActions.LoadSchedulerOptimizations(objectData.scenarioId),
            );
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $loadSchedulerOptimizations = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.LoadSchedulerOptimizations),
      switchMap((objectData: SchedulerActions.LoadSchedulerOptimizations) => {
        return this.schedulerService.getSchedulerOptimizations(objectData.scenarioId).pipe(
          switchMap((response: BaseOneResponseInterface<ISchedulerOptimizationResponse[]>) => {
            return of(new SchedulerActions.SchedulerOptimizationsLoaded(response.data));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $checkSchedulerOptimizationsExist = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.CheckSchedulerOptimizationsExist),
      switchMap((objectData: SchedulerActions.CheckSchedulerOptimizationsExist) => {
        const params: HttpParams = new HttpParams()
          .set('fields', 'status,optimizationType')
          .set('limit', '1')
          .set('sort', 'id,DESC')
          .append(
            's',
            JSON.stringify({
              status: { $eq: ESchedulerOptimizationStatus.COMPLETED },
              scenarioId: { $eq: objectData.scenarioId },
            }),
          );

        return this.schedulerService.checkSchedulerOptimizations(objectData.scenarioId, params).pipe(
          switchMap((response: BaseOneResponseInterface<ISchedulerOptimizationResponse[]>) => {
            return of(new SchedulerActions.SchedulerOptimizationsExistChecked(response.data.length > 0));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $getSchedulerOptimizationsStatus = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.LoadSchedulerOptimizationsStatus),
      switchMap((objectData: SchedulerActions.LoadSchedulerOptimizationsStatus) => {
        const params: HttpParams = new HttpParams().set('fields', 'status,optimizationType').append(
          's',
          JSON.stringify({
            status: { $in: [ESchedulerOptimizationStatus.PENDING, ESchedulerOptimizationStatus.COMPLETED] },
            scenarioId: { $eq: objectData.scenarioId },
          }),
        );

        return this.schedulerService.checkSchedulerOptimizations(objectData.scenarioId, params).pipe(
          switchMap((response: BaseOneResponseInterface<ISchedulerOptimizationResponse[]>) => {
            return of(new SchedulerActions.SchedulerOptimizationsStatusLoaded(response.data));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $updateOptimizationScheduler = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.UpdateSchedulerOptimization),
      mergeMap((objectData: SchedulerActions.UpdateSchedulerOptimization) => {
        return this.schedulerService.updateSchedulerOptimization(objectData.scenarioId, objectData.params).pipe(
          mergeMap((response: ResponseInterface<ISchedulerOptimizationResponse>) => {
            return of(new SchedulerActions.SchedulerOptimizationUpdated(response.data));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $optimizationSelectCurrentPlan = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.OptimizationSelectCurrentPlan),
      mergeMap((objectData: SchedulerActions.OptimizationSelectCurrentPlan) => {
        return this.schedulerService.optimizationSelectCurrentPlan(objectData.scenarioId).pipe(
          mergeMap((response: ResponseInterface<boolean>) => {
            return of(new SchedulerActions.OptimizationSelectCurrentPlanCompleted(response.data));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $getSchedulerKpiMetricInfo = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.KpiMetricInfoLoad),
      switchMap((objectData: SchedulerActions.KpiMetricInfoLoad) => {
        return this.schedulerService.getSchedulerKpiMetricInfo(objectData.scenarioId).pipe(
          switchMap((response: BaseOneResponseInterface<ISchedulerKpiMetricInfo>) => {
            return of(new SchedulerActions.KpiMetricInfoLoaded(response.data));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $saveSchedulerKpiCardConfigs = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.SaveSchedulerKpiCardConfigs),
      switchMap((objectData: SchedulerActions.SaveSchedulerKpiCardConfigs) => {
        return this.schedulerService
          .saveSchedulerKpiCardConfigs(objectData.payload.scenarioId, objectData.payload.config)
          .pipe(
            switchMap(() => {
              return emptyAction;
            }),
            catchError((error) => {
              return of(new SchedulerActions.FetchError(error));
            }),
          );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $checkForDeployItems = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.CheckForDeployItems),
      switchMap((objectData: SchedulerActions.CheckForDeployItems) => {
        return this.schedulerService
          .checkForDeployItems(objectData.checkForDeployItems, objectData.uncreatedWorkOrders)
          .pipe(
            switchMap((response: BaseOneResponseInterface<ICheckForDeployResponse>) => {
              return of(new SchedulerActions.CheckForDeployItemsCompleted(response.data));
            }),
            catchError((error) => {
              return of(new SchedulerActions.FetchError(error));
            }),
          );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $checkForIncompatibleWorkOrders = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.CheckForIncompatibleWorkOrders),
      switchMap((objectData: SchedulerActions.CheckForIncompatibleWorkOrders) => {
        this.store.dispatch(new AppActions.ShowLoader());
        return this.schedulerService.checkForIncompatibleWorkOrders(objectData.params).pipe(
          switchMap((response: GetManyResponseInterface<ICheckForIncompatibleWorkOrdersResponse>) => {
            return of(
              new SchedulerActions.CheckForIncompatibleWorkOrdersCompleted(response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );

  $saveScenarioUncreatedWorkOrders = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.SaveScenarioUncreatedWorkOrders),
      switchMap((objectData: SchedulerActions.SaveScenarioUncreatedWorkOrders) => {
        return this.schedulerService
          .saveUncreatedWorkOrders(objectData.scenarioId, objectData.uncreatedWorkOrders)
          .pipe(
            switchMap((response: ScenarioBulkResponseDataInterface) => {
              return of(new SchedulerActions.ScenarioUncreatedWorkOrdersSaved(response));
            }),
            catchError((error) => {
              return of(new SchedulerActions.FetchError(error));
            }),
          );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $deleteScenarioUncreatedWorkOrders = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.DeleteScenarioUncreatedWorkOrders),
      switchMap((objectData: SchedulerActions.DeleteScenarioUncreatedWorkOrders) => {
        return this.schedulerService.deleteUncreatedWorkOrders(objectData.uncreatedWorkOrderIds).pipe(
          switchMap((response: BulkResponseDataInterface) => {
            return of(new SchedulerActions.ScenarioUncreatedWorkOrdersDeleted(response));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
    ),
  );

  $getLinePathData = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.LoadLinePathData),
      switchMap((objectData: SchedulerActions.LoadLinePathData) => {
        return this.schedulerService.getLinePathData(objectData.siteId).pipe(
          switchMap((response: GetManyResponseInterface<ILinePathDetailsBySiteId>) => {
            return of(new SchedulerActions.LinePathDataLoaded(response));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $downloadSchedulerListViewExcel = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.SchedulerDownloadExcel),
      switchMap((objectData: SchedulerActions.SchedulerListViewDownloadExcel) => {
        this.schedulerService.downloadListViewExcel(objectData.payload);

        return emptyAction;
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $getOptimizationAlgorithms = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.LoadOptimizationAlgorithms),
      switchMap(() => {
        return this.schedulerService.getOptimizationAlgorithms().pipe(
          switchMap((response: BaseOneResponseInterface<IOptimizationAlgorithms[]>) => {
            return of(new SchedulerActions.OptimizationAlgorithmsLoaded(response.data));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $updateScenario = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.UpdateSchedulerScenario),
      switchMap((objectData: SchedulerActions.UpdateSchedulerScenario) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.schedulerService.updateSchedulerScenario(objectData.id, objectData.scenario).pipe(
          switchMap((response: BaseOneResponseInterface<ScenarioInterface>) => {
            return of(
              ...[
                new AppActions.HideLoader(),
                new SchedulerActions.SchedulerScenarioUpdated(response),
                new SchedulerActions.ClearSchedulerData(),
                ...(objectData.isProductionScheduler ? [new SchedulerActions.KpiMetricInfoLoad(objectData.id)] : []),
              ],
            );
          }),
          catchError((error) => {
            return of(new AppActions.HideLoader(), new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );

  $loadProductFamilyColors = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.LoadProductFamilyColors),
      switchMap((objectData: SchedulerActions.LoadProductFamilyColors) => {
        const params = new HttpParams()
          .set('s', JSON.stringify({ siteId: { $eq: objectData.siteId } }))
          .set('limit', '5000');

        return this.productFamilyService.getProductFamily(params).pipe(
          switchMap((response: GetManyResponseInterface<IProductFamily>) => {
            return of(new SchedulerActions.ProductFamilyColorsLoaded(response.data));
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error));
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error));
      }),
    ),
  );

  $saveSelectedOptimizationItems = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerActions.SchedulerActionTypes.SaveSelectedOptimizationItems),
      switchMap((objectData: SchedulerActions.SaveSelectedOptimizationItems) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.schedulerService.saveScenarioItems(objectData.scenarioId, objectData.scenarioItems).pipe(
          switchMap((response) => {
            return of(new SchedulerActions.SelectedOptimizationItemsSaved(response), new AppActions.HideLoader());
          }),
          catchError((error) => {
            return of(new SchedulerActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((error) => {
        return of(new SchedulerActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );
}
